import { IconPlus } from "@tabler/icons-react";
import { RiGeminiLine } from "@remixicon/react";
import { useGlobalStore } from "@/GlobalStore";
import DiagonalShimmer from "@/components/common/loaders/DiagonalShimmer";
import { uploadFilesAsync, updateAccountLogos } from "@/api/globalApiService";
import { Logo } from "@/types";
import { withCacheBuster } from "@/components/common/adCard/Media";
import { useState, useEffect } from "react";
import { updateAccountInList } from "@/screens/utils/Helpers";

const MAXIMUM_LOGOS = 5;

type Props = {
  onImageSelect: (params: {
    token: number;
    src: string;
    naturalWidth: number;
    naturalHeight: number;
  }) => void;
  nextAddImageToken: number;
  setNextAddImageToken: (updater: number | ((prev: number) => number)) => void;
};

type LogoWithDimensions = Logo & {
  naturalWidth?: number;
  naturalHeight?: number;
  isUploading?: boolean;
};

const BrandLogoLibrary = ({
  onImageSelect,
  nextAddImageToken,
  setNextAddImageToken,
}: Props) => {
  const { selectedAccount, setSelectedAccount, accountList, setAccountList } =
    useGlobalStore();
  const [logos, setLogos] = useState<LogoWithDimensions[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Initialize logos from selectedAccount
  useEffect(() => {
    if (selectedAccount?.logos) {
      setLogos(
        selectedAccount.logos.map((logo) => ({
          ...logo,
          naturalWidth: undefined,
          naturalHeight: undefined,
          isUploading: false,
        })),
      );
    } else {
      setLogos([]);
    }
  }, [selectedAccount?.logos]);

  const handleLogoUpload = async (files: File[]) => {
    if (!selectedAccount) return;

    const remainingSlots = MAXIMUM_LOGOS - logos.length;
    const filesToUpload = files.slice(0, remainingSlots);

    if (filesToUpload.length === 0) return;

    setIsUploading(true);

    // Create temp IDs for all files to track placeholders
    const baseTime = Date.now();
    const tempIds = filesToUpload.map(
      (_, index) => `temp-${baseTime}-${index}-${Math.random().toString(36).slice(2, 9)}`,
    );

    try {
      // Insert shimmer placeholders at the start for all files
      setLogos((prev) => {
        const next = [...prev];
        const placeholders = tempIds.map((tempId) => ({
          file_id: tempId,
          url: "",
          isUploading: true,
        }));
        next.unshift(...placeholders);
        return next;
      });

      // Upload all files in a single API call
      const response = await uploadFilesAsync(filesToUpload, selectedAccount.id || "");
      if (response.files?.length) {
        const newLogos: Logo[] = response.files.map((file, index) => ({
          file_id: file.id,
          url: file.public_url,
          type: filesToUpload[index].type,
        }));

        // Update account logos on server
        // Put new logos at the start to maintain the order (newest first)
        const currentLogoIds = logos.filter((l) => !l.isUploading).map((l) => l.file_id);
        const newLogoIds = newLogos.map((logo) => logo.file_id);
        const updatedLogoIds = [...newLogoIds, ...currentLogoIds];

        const updateResponse = await updateAccountLogos(
          selectedAccount.id,
          updatedLogoIds,
        );

        // Update global store
        const updatedAccount = {
          ...selectedAccount,
          logos: updateResponse.logos,
        };
        setSelectedAccount(updatedAccount);
        // Update account in list
        updateAccountInList(accountList, updatedAccount, setAccountList);

        // Replace all temp placeholders with real logos
        setLogos((prev) => {
          const list = [...prev];
          return list.map((l) => {
            const tempIndex = tempIds.indexOf(l.file_id);
            if (tempIndex !== -1 && newLogos[tempIndex]) {
              return {
                file_id: newLogos[tempIndex].file_id,
                url: newLogos[tempIndex].url,
                type: newLogos[tempIndex].type,
              };
            }
            return l;
          });
        });
      } else {
        // If no files returned, remove all temp placeholders
        setLogos((prev) => prev.filter((l) => !tempIds.includes(l.file_id)));
      }
    } catch (error) {
      console.error("Failed to upload brand logos:", error);
      // If failed, remove all temp placeholders
      setLogos((prev) => prev.filter((l) => !tempIds.includes(l.file_id)));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveLogo = async (logoId: string) => {
    if (!selectedAccount) return;

    try {
      // Optimistically remove
      setLogos((prev) => prev.filter((l) => l.file_id !== logoId));

      // Update account logos on server
      const updatedLogoIds = logos
        .filter((l) => l.file_id !== logoId && !l.isUploading)
        .map((l) => l.file_id);

      const updateResponse = await updateAccountLogos(selectedAccount.id, updatedLogoIds);

      // Update global store
      const updatedAccount = {
        ...selectedAccount,
        logos: updateResponse.logos,
      };
      setSelectedAccount(updatedAccount);
      // Update account in list
      updateAccountInList(accountList, updatedAccount, setAccountList);
    } catch (error) {
      console.error("Failed to remove brand logo:", error);
      // On failure, refetch from account
      if (selectedAccount?.logos) {
        setLogos(
          selectedAccount.logos.map((logo) => ({
            ...logo,
            naturalWidth: undefined,
            naturalHeight: undefined,
            isUploading: false,
          })),
        );
      }
    }
  };

  return (
    <div className="mt-5">
      <div className="mb-1 flex items-center gap-[6px]">
        <RiGeminiLine size={14} className="text-brand-primary-400 shrink-0" />
        <span className="text-[12px] text-brand-primary-800">Brand Logos</span>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {/* Add logo tile - First position */}
        {logos.length < MAXIMUM_LOGOS && (
          <button
            className="relative w-full aspect-square rounded overflow-hidden border border-dashed border-gray-300 hover:border-gray-400 flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";
              input.multiple = true; // Allow multiple file selection
              input.onchange = () => {
                const files = Array.from(input.files || []);
                if (files.length === 0) return;
                void handleLogoUpload(files);
              };
              input.click();
            }}
            title="Add brand logo"
            disabled={isUploading}
          >
            <IconPlus className="w-5 h-5 text-gray-500" />
          </button>
        )}

        {/* Existing brand logos */}
        {logos.map((logo) => (
          <div
            key={logo.file_id}
            className="relative group w-full aspect-square rounded overflow-hidden border border-gray-300 hover:border-gray-400 bg-[rgb(201,201,201)]"
          >
            {/* Delete button */}
            {!logo.isUploading && (
              <button
                className="absolute top-[0.5px] right-[0.5px] z-20 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-150 w-4 h-4 flex items-center justify-center"
                title="Delete"
                aria-label="Delete brand logo"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleRemoveLogo(logo.file_id);
                }}
              >
                {/* Use a plain SVG fallback for reliable render */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 11H11V17H9V11ZM13 11H15V17H13V11ZM9 4V6H15V4H9Z"></path>
                </svg>
              </button>
            )}

            <button
              className="absolute inset-0 z-10"
              onClick={(e) => {
                if (logo.isUploading || !logo.url) return;
                e.stopPropagation();
                // Use cached dimensions instead of downloading image again
                setNextAddImageToken((t) => t + 1);
                onImageSelect({
                  token: nextAddImageToken + 1,
                  src: logo.url,
                  naturalWidth: logo.naturalWidth || 200, // Use cached or default
                  naturalHeight: logo.naturalHeight || 200, // Use cached or default
                });
              }}
              disabled={Boolean(logo.isUploading || !logo.url)}
            />

            {logo.isUploading ? (
              <DiagonalShimmer colors={["#F6F6F6", "#BFBFBF", "#FFFFFF"]} duration={3} />
            ) : (
              <img
                crossOrigin="anonymous"
                src={withCacheBuster(logo.url)}
                alt="brand logo"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                draggable={false}
                onLoad={(e) => {
                  // Cache dimensions when preview loads
                  const img = e.currentTarget;
                  setLogos((prev) =>
                    prev.map((item) =>
                      item.file_id === logo.file_id
                        ? {
                            ...item,
                            naturalWidth: img.naturalWidth,
                            naturalHeight: img.naturalHeight,
                          }
                        : item,
                    ),
                  );
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BrandLogoLibrary;
